import React, { useEffect, useState } from 'react';
import { Eye, RefreshCw, Search, ShieldAlert, Archive, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, fmtDate } from '../../../lib/adminApi';

const MONTH = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const ImpersonateHubTab = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [impersonating, setImpersonating] = useState('');
  const [diagEmail, setDiagEmail] = useState('');
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [busyTactical, setBusyTactical] = useState(''); // id of the row currently being archived/restored/deleted

  const search = async (term = q) => {
    setLoading(true);
    try {
      const data = await adminApi.searchUsers(term);
      setUsers(Array.isArray(data) ? data : (data?.users || []));
    } catch (err) {
      toast.error(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(''); /* eslint-disable-next-line */ }, []);

  const impersonate = async (u) => {
    setImpersonating(u.id);
    try {
      const res = await adminApi.impersonate(u.id);
      if (!res?.token) throw new Error('No impersonation token returned');
      const adminToken = localStorage.getItem('tactical_token');
      sessionStorage.setItem('admin_return_token', adminToken);
      sessionStorage.setItem('impersonating_user', JSON.stringify({ id: u.id, email: u.email, display_name: u.display_name }));
      localStorage.setItem('tactical_token', res.token);
      toast.success(`Now impersonating ${u.email}`);
      window.location.href = '/';
    } catch (err) {
      toast.error(err.message || 'Impersonation failed');
    } finally {
      setImpersonating('');
    }
  };

  const runDiagnostic = async (emailOverride) => {
    const email = (emailOverride || diagEmail).trim();
    if (!email) return;
    setDiagLoading(true);
    if (!emailOverride) setDiag(null);
    try {
      const r = await adminApi.diagnoseUser(email);
      setDiag(r);
      if (r?.total_tacticals === 0) toast.info('No tacticals found for this email');
    } catch (err) {
      toast.error(err.message || 'Diagnostic failed');
    } finally {
      setDiagLoading(false);
    }
  };

  const archiveTactical = async (t) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Archive this ${MONTH[t.month - 1]} ${t.year} tactical? It will appear under the user's Past Tacticals.`)) return;
    setBusyTactical(t.id);
    try {
      const r = await adminApi.archiveTactical(t.id);
      toast.success(r.message || 'Archived');
      await runDiagnostic(diag?.email);
    } catch (err) {
      toast.error(err.message || 'Archive failed');
    } finally {
      setBusyTactical('');
    }
  };

  const restoreTactical = async (t) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Restore this archived tactical to active? If a conflicting active tactical exists for the same month, it will be archived. Continue?')) return;
    setBusyTactical(t.id);
    try {
      const r = await adminApi.restoreTactical(t.id);
      toast.success(r.message || 'Restored');
      await runDiagnostic(diag?.email);
    } catch (err) {
      toast.error(err.message || 'Restore failed');
    } finally {
      setBusyTactical('');
    }
  };

  const deleteTactical = async (t) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`PERMANENTLY delete this ${MONTH[t.month - 1]} ${t.year} tactical? This cannot be undone.`)) return;
    setBusyTactical(t.id);
    try {
      const r = await adminApi.deleteTactical(t.id);
      toast.success(r.message || 'Deleted');
      await runDiagnostic(diag?.email);
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setBusyTactical('');
    }
  };

  return (
    <div data-testid="tab-impersonate" className="space-y-5">
      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-800 leading-relaxed">
          <strong>Impersonation logs you in as another user.</strong> Use it only for support. Your actions while impersonating are tagged as theirs. Click "Return to Admin" in the app to come back.
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Find user</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="Name or email…"
              data-testid="impersonate-search"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-violet-400"
            />
          </div>
          <button onClick={() => search()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Search
          </button>
        </div>
        <div className="mt-3 max-h-72 overflow-auto rounded-md border border-slate-100">
          {!loading && users.length === 0 && (
            <div className="text-xs text-slate-400 py-4 text-center italic">No users found.</div>
          )}
          {users.slice(0, 50).map((u) => (
            <div key={u.id} className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0 border-slate-100 hover:bg-slate-50">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-400 flex items-center justify-center text-white text-[10px] font-bold">
                {(u.display_name || u.email || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{u.display_name || u.email}</div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
              </div>
              <button
                onClick={() => impersonate(u)}
                disabled={impersonating === u.id}
                data-testid={`impersonate-${u.email}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded border border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-50"
              >
                <Eye className="w-3 h-3" /> {impersonating === u.id ? '…' : 'Impersonate'}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-bold text-slate-900 mb-1">User Diagnostic</div>
        <div className="text-xs text-slate-500 mb-3">
          Find every tactical owned by an email — across ghost accounts. Use it to triage missing data, archive duplicates, restore archived months, or delete bad data.
        </div>
        <div className="flex gap-2">
          <input
            value={diagEmail}
            onChange={(e) => setDiagEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runDiagnostic()}
            placeholder="user@therapymgmt.com"
            data-testid="diagnostic-email"
            className="flex-1 px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-violet-400"
          />
          <button
            onClick={() => runDiagnostic()}
            disabled={diagLoading || !diagEmail.trim()}
            data-testid="diagnostic-run"
            className="px-3 py-2 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {diagLoading ? 'Searching…' : 'Diagnose'}
          </button>
        </div>

        {diag && (
          <div className="mt-4 space-y-4" data-testid="diagnostic-result">
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-700 mb-1.5">
                User Accounts Found: {diag.user_records?.length || 0}
              </div>
              {(diag.user_records || []).map((u) => (
                <div key={u.id || u.email} className="text-[11px] text-slate-600 leading-relaxed">
                  <span className="font-mono">{u.id}</span> — {u.display_name || '—'} ({u.email})
                  {diag.user_records.length > 1 && (
                    <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Ghost</span>
                  )}
                </div>
              ))}
            </div>

            <div className="text-xs font-semibold text-slate-700">Tacticals Found: {diag.total_tacticals}</div>
            {diag.tacticals?.length === 0 ? (
              <p className="text-xs text-slate-500">No tacticals found for this email.</p>
            ) : (
              <div className="space-y-2">
                {diag.tacticals.map((t) => {
                  const sitesEmpty = (t.facility_sites || []).every((s) => s.items_with_data === 0);
                  const scorecardOk = t.scorecard_filled && t.scorecard_filled !== '0/0' && !t.scorecard_filled.startsWith('0/');
                  return (
                    <div key={t.id} className={`p-3 rounded-lg border ${t.is_archived ? 'bg-slate-50 border-slate-200' : 'bg-white border-cyan-300/60'}`} data-testid={`diag-tactical-${t.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{MONTH[t.month - 1]} {t.year}</span>
                          {t.is_archived
                            ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">Archived</span>
                            : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Active</span>}
                          {t.has_tah && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-slate-300 text-slate-600">TAH</span>}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {!t.is_archived && (
                            <button
                              onClick={() => archiveTactical(t)}
                              disabled={busyTactical === t.id}
                              data-testid={`archive-btn-${t.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                            >
                              <Archive className="w-3 h-3" /> {busyTactical === t.id ? '…' : 'Archive'}
                            </button>
                          )}
                          {t.is_archived && (
                            <button
                              onClick={() => restoreTactical(t)}
                              disabled={busyTactical === t.id}
                              data-testid={`restore-btn-${t.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                            >
                              <RotateCcw className="w-3 h-3" /> {busyTactical === t.id ? '…' : 'Restore'}
                            </button>
                          )}
                          <button
                            onClick={() => deleteTactical(t)}
                            disabled={busyTactical === t.id}
                            data-testid={`delete-btn-${t.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                          >
                            <Trash2 className="w-3 h-3" /> {busyTactical === t.id ? '…' : 'Delete'}
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                        <div className={`px-2 py-1 rounded ${t.has_development_data ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
                          Dev: {t.has_development_data ? 'Has Data' : 'Empty'}
                        </div>
                        <div className={`px-2 py-1 rounded ${scorecardOk ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
                          Scorecard: {t.scorecard_filled}
                        </div>
                        <div className={`px-2 py-1 rounded ${t.has_lightning_round ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
                          Lightning: {t.has_lightning_round ? 'Has Data' : 'Empty'}
                        </div>
                        <div className={`px-2 py-1 rounded ${!sitesEmpty ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
                          Sites: {(t.facility_sites || []).map((s) => `${s.name}(${s.items_with_data}/${s.total_items})`).join(', ') || 'None'}
                        </div>
                      </div>
                      <div className="mt-1 text-[10px] text-slate-400 leading-relaxed">
                        user_id: <span className="font-mono">{t.user_id}</span> · id: <span className="font-mono">{(t.id || '').slice(0, 8)}…</span> · updated: {t.updated_at ? fmtDate(t.updated_at) : 'n/a'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
